import Image from "next/image";

import type { SanityImageSource } from "@sanity/image-url";
import cn from "classnames";

import type { GetPageQueryResult } from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

/**
 * Container child block for a standalone image.
 *
 * Defaults to filling the parent container while preserving the asset's
 * intrinsic aspect ratio. Optional width/height pin the frame in pixels.
 */
export function ImageBuilder({ block }: ImageBuilderProps) {
  if (!block?.image?.asset) return null;

  const width = block.width ?? undefined;
  const height = block.height ?? undefined;
  const fillsWidth = width == null;
  const fillsHeight = height == null;
  const intrinsicWidth = block.image.dimensions?.width ?? 1;
  const intrinsicHeight = block.image.dimensions?.height ?? 1;

  const src = urlFor(block.image as SanityImageSource)
    .width(width ?? 1920)
    .fit("max")
    .url();
  if (!src) return null;

  const alignment = block.alignment ?? "left";
  const rounded = block.rounded ?? "none";
  const alt = block.image.alt?.trim() || "";

  return (
    <figure
      data-testid="image-builder"
      className={cn("min-h-0", {
        "w-full": fillsWidth,
        "h-full": fillsHeight,
        "overflow-hidden": rounded !== "none",
        "rounded-lg": rounded === "small",
        "rounded-xl": rounded === "medium",
        "rounded-2xl": rounded === "large",
        "mr-auto": !fillsWidth && alignment === "left",
        "mx-auto": !fillsWidth && alignment === "center",
        "ml-auto": !fillsWidth && alignment === "right",
      })}
      style={{
        width: fillsWidth ? undefined : width,
        height: fillsHeight ? undefined : height,
      }}>
      <Image
        src={src}
        alt={alt}
        width={intrinsicWidth}
        height={intrinsicHeight}
        className="h-full w-full object-cover"
        sizes="100vw"
      />
    </figure>
  );
}

type PageBuilderBlock = NonNullable<
  NonNullable<GetPageQueryResult>["pageBuilder"]
>[number];

type ContainerBuilderBlock = Extract<
  PageBuilderBlock,
  { _type: "containerBuilder" }
>;

export type ImageBuilderBlock = Extract<
  NonNullable<ContainerBuilderBlock["children"]>[number],
  { _type: "imageBuilder" }
>;

type ImageBuilderProps = {
  block: ImageBuilderBlock;
};
